import React, { useRef, useState, useCallback } from "react";
import styles from "./portfolioGallery.module.css";

/**
 * Swipeable one-photo-at-a-time gallery.
 *
 * photos: array of either strings (image URLs) or objects { src, alt }
 *
 * <Gallery photos={[img1, img2, img3]} />
 * <Gallery photos={[{ src: img1, alt: "Chrome French tips" }]} />
 */
function PortfolioGallery({ photos = [] }) {
  const trackRef = useRef(null);
  const frameRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleScroll = useCallback(() => {
    // Throttle to one update per animation frame
    if (frameRef.current) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      const track = trackRef.current;
      if (!track || !track.clientWidth) return;
      setActiveIndex(Math.round(track.scrollLeft / track.clientWidth));
    });
  }, []);

  const goTo = (index) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollTo({ left: index * track.clientWidth, behavior: "smooth" });
  };

  if (!photos.length) return null;

  return (
    <div className={styles.gallery}>
      <div
        className={styles.track}
        ref={trackRef}
        onScroll={handleScroll}
        role="region"
        aria-label="Photo gallery"
        tabIndex={0}
      >
        {photos.map((photo, i) => {
          const src = typeof photo === "string" ? photo : photo.src;
          const alt =
            typeof photo === "string" ? `Photo ${i + 1}` : photo.alt || `Photo ${i + 1}`;
          return (
            <div className={styles.slide} key={src + i}>
              <img
                className={styles.image}
                src={src}
                alt={alt}
                loading={i === 0 ? "eager" : "lazy"}
                draggable={false}
              />
            </div>
          );
        })}
      </div>

      {photos.length > 1 && (
        <div className={styles.dots}>
          {photos.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`${styles.dot} ${i === activeIndex ? styles.dotActive : ""}`}
              onClick={() => goTo(i)}
              aria-label={`Go to photo ${i + 1} of ${photos.length}`}
              aria-current={i === activeIndex}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default PortfolioGallery;
